import {Component, computed, effect, inject, input, model, output, signal, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DialogComponent} from '@components/dialogs/dialog/dialog.component';
import {IconBoldCloseSquareComponent} from '@components/icons/icon-bold-close-square/icon-bold-close-square.component';
import {IconLocationAddComponent} from '@components/icons/icon-location-add/icon-location-add.component';
import {Anexo} from '@core/models/anexo';
import {DadosLocalizacaoDemanda, DemandaResponse, ReforcarDemandaPayload} from '@core/models/demanda';
import {AuthService} from '@core/services/auth.service';
import {DemandaService} from '@core/services/demanda.service';
import {TipoRepresentacaoService} from '@core/services/tipo-representacao.service';
import {
    DadosLocalizacaoDemandaComponent
} from '@pages/demandas/dados-localizacao-demanda/dados-localizacao-demanda.component';
import {FormAnexosDemandaComponent} from '@pages/demandas/form-anexos-demanda/form-anexos-demanda.component';
import {FormDescricaoDemandaComponent} from '@pages/demandas/form-descricao-demanda/form-descricao-demanda.component';
import {
    FormRepresentacaoGovbrComponent
} from '@pages/demandas/form-representacao-govbr/form-representacao-govbr.component';
import {MSG_REQUIRED_FIELDS_FORM} from '@shared/constants/messages';
import {ID_REPRESENTACAO_KEY} from '@shared/constants/storage-keys';
import {MessageService} from 'primeng/api';
import {ButtonModule} from 'primeng/button';

@Component({
    selector: 'app-visualizar-reforcar-demanda-dialog',
    imports: [
        ReactiveFormsModule,
        ButtonModule,
        DialogComponent,
        DadosLocalizacaoDemandaComponent,
        IconLocationAddComponent,
        FormDescricaoDemandaComponent,
        FormAnexosDemandaComponent,
        IconBoldCloseSquareComponent,
        FormRepresentacaoGovbrComponent
    ],
    templateUrl: './visualizar-reforcar-demanda-dialog.component.html',
})
export class VisualizarReforcarDemandaDialog {
    readonly visible = model.required<boolean>();
    readonly demandaId = input.required<number | null>();
    readonly editMode = input.required<boolean>();
    readonly mode = input.required<'visualizar' | 'reforcar'>();
    readonly refresh = output<boolean>();

    @ViewChild(FormRepresentacaoGovbrComponent)
    protected formRepresentacao?: FormRepresentacaoGovbrComponent;

    @ViewChild(FormAnexosDemandaComponent)
    protected formAnexos?: FormAnexosDemandaComponent;

    protected showConfirmReforcarDialog = signal(false);
    protected form!: FormGroup;
    protected readonly anexos = signal<Anexo[]>([]);
    protected readonly uploadEmAndamento = signal(false);
    protected submitting = false;

    protected readonly demanda = signal<DemandaResponse | null>(null);
    protected readonly dadosLocalizacaoDemanda = signal<DadosLocalizacaoDemanda | null>(null);
    protected readonly tipoObraId = computed(() => this.demanda()?.idTipoObra ?? null);
    protected readonly headerTitle = computed(() =>
        this.mode() === 'reforcar' ? 'Reforçar Demanda' : 'Visualizar Demanda'
    );
    private readonly tipoRepresentacaoService = inject(TipoRepresentacaoService);

    private readonly fb = inject(FormBuilder);
    private readonly demandaService = inject(DemandaService);
    private readonly authService = inject(AuthService);
    protected readonly exibirFormularioRepresentacaoGovbr = computed(() =>
        this.mode() === 'reforcar' && this.authService.isAuthenticatedGovBr()
    );
    private readonly messageService = inject(MessageService);

    constructor() {
        effect(() => {
            const id = this.demandaId();
            if (id) {
                this.getDemanda(id);
            }
        });
    }

    protected onAnexosChange(anexos: Anexo[]): void {
        this.anexos.set(anexos);
    }

    protected onUploadStatusChange(inProgress: boolean): void {
        this.uploadEmAndamento.set(inProgress);
    }

    protected onReforcar(): void {
        this.form.markAllAsTouched();
        this.formAnexos?.markAllAsTouched();
        if (this.exibirFormularioRepresentacaoGovbr()) {
            this.formRepresentacao?.form?.markAllAsTouched();
        }

        const representacaoInvalida = this.exibirFormularioRepresentacaoGovbr() && (
            !this.formRepresentacao?.form?.get('tipoRepresentacao')?.getRawValue() ||
            (!this.formRepresentacao?.isCidadaoSelecionado && this.formRepresentacao?.form?.invalid)
        );
        const anexosInvalidos = this.formAnexos ? !this.formAnexos.isValid() : false;
        const formInvalido = this.form.invalid;

        if (representacaoInvalida || anexosInvalidos || formInvalido) {
            this.messageService.add({severity: 'warn', summary: 'Atenção!', detail: MSG_REQUIRED_FIELDS_FORM});
            return;
        }

        this.showConfirmReforcarDialog.set(true);
    }

    protected onSubmit(): void {
        if (this.form.invalid) {
            this.messageService.add({severity: 'warn', summary: 'Atenção!', detail: MSG_REQUIRED_FIELDS_FORM});
            this.form.markAllAsTouched();
            return;
        }
        // GovBR com tipo diferente de CIDADÃO: primeiro cadastra representação, depois reforça
        if (this.exibirFormularioRepresentacaoGovbr() && this.formRepresentacao && !this.formRepresentacao.isCidadaoSelecionado) {
            this.submitting = true;
            this.tipoRepresentacaoService.save(this.formRepresentacao.buildFormData()).subscribe({
                next: (response) => {
                    this.formRepresentacao!.setStorageRepresentacao(response.id);
                    this.salvarReforcoDemanda(response.id ?? null);
                },
                error: (error: any) => {
                    this.submitting = false;
                    this.showConfirmReforcarDialog.set(false);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro!',
                        detail: error?.error?.message || 'Falha ao cadastrar representação.',
                    });
                },
            });
            return;
        }

        this.salvarReforcoDemanda(null);
    }

    protected onCancelar(): void {
        this.resetVisibleDialogs();
    }

    private salvarReforcoDemanda(idRepresentacaoGovbr: number | null): void {
        const demandaId = this.demandaId();
        if (demandaId == null) return;

        const payload = this.buildReforcarPayload(idRepresentacaoGovbr);
        this.submitting = true;
        this.demandaService.reforcarDemanda(demandaId, payload).subscribe({
            next: () => {
                this.submitting = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso!',
                    detail: 'Demanda reforçada com sucesso.'
                });
                this.refresh.emit(true);
                this.resetVisibleDialogs();
            },
            error: () => {
                this.submitting = false;
                this.messageService.add({severity: 'error', summary: 'Erro!', detail: 'Falha ao reforçar demanda.'});
                this.showConfirmReforcarDialog.set(false);
            },
        });
    }

    private initForm(): void {
        const reforcar = this.mode() === 'reforcar';
        const disabled = !reforcar && !this.editMode();
        const demanda = reforcar ? null : this.demanda();
        this.form = this.fb.group({
            tipoRepresentacao: [{value: demanda?.tipoRepresentacao ?? null, disabled}, Validators.required],
            nomeSolicitante: [{value: demanda?.nomeSolicitante || '', disabled}, Validators.required],
            justificativa: [{value: demanda?.justificativa || '', disabled}, Validators.required],
            existeProcessoSei: [{value: demanda?.possuiNumeroSei || false, disabled}],
            numeroProcessoSei: [{value: demanda?.numeroProcessoSei || null, disabled}],
            tipoProcessoSei: [{value: demanda?.tipoProcesso || null, disabled}],
            descricaoProcesso: [{value: demanda?.descricaoProcesso || null, disabled}],
            representacaoUsuarioConcessionaria: [{
                value: demanda?.representacaoUsuarioConcessionaria || null,
                disabled: true
            }],
        });
    }

    private getDemanda(id: number): void {
        this.demandaService.buscarDemandaPorId(id).subscribe({
            next: (response) => {
                this.demanda.set(response);
                this.setDadosLocalizacaoDemanda(response);
                this.initForm();
            },
            error: () => {
            },
        });
    }

    private setDadosLocalizacaoDemanda(demanda: DemandaResponse): void {
        this.dadosLocalizacaoDemanda.set({
            longitude: demanda.longitude,
            latitude: demanda.latitude,
            municipio: demanda.cidade,
            codRodovia: demanda.codigoRodovia,
            concessionaria: demanda.concessionaria,
            data: demanda.data,
        });
    }

    private buildReforcarPayload(idRepresentacaoGovbr: number | null): ReforcarDemandaPayload {
        const localizacao = this.dadosLocalizacaoDemanda();
        const tipoRepresentacao = this.form.get('tipoRepresentacao')?.value;
        const tipoProcessoSei = this.form.get('tipoProcessoSei')?.value;
        const justificativa = this.form.get('justificativa')?.value ?? '';
        const representacaoUsuarioConcessionaria = this.form.get('representacaoUsuarioConcessionaria')?.value;
        const representacaoUsuarioGovbr = this.formRepresentacao?.tipoRepresentacaoSelecionado;

        const isConcessionaria = this.authService.isAuthenticatedConcessionaria();
        const isGovBr = this.authService.isAuthenticatedGovBr();

        let idTipoRepresentacao: number | null = null;
        if (!isConcessionaria) {
            idTipoRepresentacao = isGovBr ? (representacaoUsuarioGovbr?.id ?? null) : (tipoRepresentacao?.id ?? null);
        }

        return {
            descricao: justificativa,
            dadosLocalizacao: {
                longitude: localizacao?.longitude ?? null,
                latitude: localizacao?.latitude ?? null,
                municipio: localizacao?.municipio ?? null,
                codRodovia: localizacao?.codRodovia ?? null,
                concessionaria: localizacao?.concessionaria ?? null,
            },
            dadosDescricao: {
                idRepresentacao: isGovBr
                    ? idRepresentacaoGovbr
                    : (JSON.parse(sessionStorage.getItem(ID_REPRESENTACAO_KEY) ?? 'null') as number | null),
                idTipoRepresentacao,
                nomeSolicitante: this.form.get('nomeSolicitante')?.value ?? null,
                justificativa,
                numeroProcessoSei: this.form.get('numeroProcessoSei')?.value ?? null,
                possuiNumeroSei: this.form.get('existeProcessoSei')?.value ?? null,
                idTipoProcesso: tipoProcessoSei?.id ?? null,
                descricaoProcesso: this.form.get('descricaoProcesso')?.value ?? null,
                representacaoUsuarioConcessionaria: isConcessionaria ? (representacaoUsuarioConcessionaria ?? null) : null,
            },
            infoArquivos: this.anexos().map((anexo) => ({
                idArquivo: anexo.idArquivo,
                idTipoAnexo: anexo.idTipoAnexo,
                nomeOriginal: anexo.nomeOriginal,
                nomeUnicoIdentificador: anexo.nomeUnicoIdentificador,
                descricaoOutros: anexo.descricaoOutros,
                extensao: anexo.extensao,
                tamanho: anexo.tamanho,
            })),
        };
    }

    private resetVisibleDialogs(): void {
        this.showConfirmReforcarDialog.set(false);
        this.visible.set(false);
    }
}
